/**
 * What the four Billing lists share — Wave 15D (C14 §10.6 items 2 and 7,
 * ADR-0033; T-6.16): the client-name lookup every list joins once, the client
 * and year filter chips, the money cell, and the footer every Billing dialog
 * ends with (the Branches dialog's).
 */

import { Button } from '@fiveox/ui'
import type { ReactNode } from 'react'

import type { ListFilter } from '../../components/page'
import { type Invoice, rupees } from '../../lib/api/billing'
import { type ClientRecord, clients } from '../../lib/api/clients'

/** The clients API caps `limit` at 200 (`app/api/clients.py`). Asking for
 *  more is a 400, and every billing screen asked for 500 — so the client
 *  list was empty on all of them and the New invoice dialog said only
 *  "The clients and services could not be loaded." Found by the Wave 4
 *  close's walk; the suites mock the client, so nothing here could see it. */
export const CLIENT_PAGE = 200

/** The firm's active clients, once, for the lists to join names from. One
 *  read serves the page — a list that fetched its client per row is the N+1
 *  that makes a billing screen feel slow. */
export async function activeClients(): Promise<ClientRecord[]> {
  const book = await clients.list({ status: 'active', limit: CLIENT_PAGE })
  return book.data
}

export function namesOf(book: ClientRecord[]): Record<string, string> {
  return Object.fromEntries(book.map((c) => [c.id, c.legal_name]))
}

/** The client chip: only the clients the list actually holds, by name. */
export function clientFilter<T extends { client_id: string }>(
  rows: T[],
  names: Record<string, string>,
): ListFilter<T> {
  const ids = [...new Set(rows.map((row) => row.client_id))]
  return {
    key: 'client',
    label: 'Client',
    allLabel: 'Every client',
    options: ids
      .map((id) => ({ value: id, label: names[id] ?? 'A client no longer active' }))
      .sort((a, b) => a.label.localeCompare(b.label)),
    match: (row, value) => row.client_id === value,
  }
}

/** The year chip, from the financial year the server stamped on each row
 *  (`fy`, written `2026-27`). A draft has none and is found under *Drafts*. */
export function yearFilter<T extends { fy: string | null }>(rows: T[]): ListFilter<T> {
  const years = [...new Set(rows.map((row) => row.fy).filter((fy): fy is string => Boolean(fy)))]
  return {
    key: 'year',
    label: 'Year',
    allLabel: 'Every year',
    options: years.sort().reverse().map((fy) => ({ value: fy, label: `FY ${fy}` })),
    match: (row, value) => row.fy === value,
  }
}

/** Whether an invoice is owed past its due date — `today` is `YYYY-MM-DD`. */
export function isOverdue(invoice: Invoice, today: string): boolean {
  return (
    invoice.status === 'issued' &&
    (invoice.payment_state === 'unpaid' || invoice.payment_state === 'part_paid') &&
    Boolean(invoice.due_date) &&
    invoice.due_date! < today
  )
}

export function invoiceStatus(invoice: Invoice, today: string): string {
  return isOverdue(invoice, today) ? 'overdue' : invoice.payment_state
}

/** A rupee figure in a table cell — the server's decimal string, formatted,
 *  never parsed. */
export function Money({ value, muted }: { value: string | null | undefined; muted?: boolean }) {
  return (
    <span className={`tabular-nums whitespace-nowrap ${muted ? 'text-muted-foreground' : ''}`}>
      {rupees(value)}
    </span>
  )
}

/** A dialog's closing row, as the Branches dialog lays it out: Cancel, then
 *  the one action, right-aligned — stacked with the action on top on a phone. */
export function DialogFooter({
  onCancel,
  busy,
  children,
}: {
  onCancel: () => void
  busy?: boolean
  children: ReactNode
}) {
  return (
    <div className="mt-6 flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
      <Button variant="ghost" onClick={onCancel} disabled={busy}>
        Cancel
      </Button>
      {children}
    </div>
  )
}
